import type { ApiReferenceTranslations } from '@scalar/types/api-reference'

export const en = {
  common: {
    streamItem: 'Stream item',
    description: 'Description',
    httpMethod: 'HTTP Method',
    path: 'Path',
    copyDefault: 'Copy default value',
    copyExample: 'Copy example value',
  },
  search: {
    label: 'Search',
    inputLabel: 'Enter search query',
    open: 'Open Search',
    placeholder: 'Search...',
    clear: 'Clear Search',
    noResults: 'No results found',
    keyboardShortcut: 'Keyboard Shortcut:',
    command: 'Command',
    control: 'Control',
    results: 'Reference Search Results',
    navigate: 'Navigate',
    select: 'Select',
    instructions: 'Press up arrow / down arrow to navigate, enter to select, type to filter results',
    entryHeading: 'Heading',
    entryOperation: 'Operation',
    entryTag: 'Tag',
    entryTagGroup: 'Tag Group',
    entryWebhook: 'Webhook',
  },
  navigation: {
    introduction: 'Introduction',
    closeGroup: 'Close Group',
    closeMenu: 'Close Menu',
    openGroup: 'Open Group',
    openMenu: 'Open Menu',
    operations: 'Operations',
    endpoints: '{name} endpoints',
    showAllEndpoints: 'Show all {name} endpoints',
    sidebarFor: 'Sidebar for {name}',
    mainContent: 'API documentation for {name}',
    collapsed: 'Collapsed',
    webhooks: 'Webhooks',
    channels: 'Channels',
  },
  server: {
    label: 'Server',
    select: 'Select a server',
  },
  info: {
    termsOfService: 'Terms of Service',
  },
  asyncapi: {
    servers: 'Servers',
    protocols: 'Protocols',
  },
  clientLibraries: {
    heading: 'Client Libraries',
    more: 'More',
    selectAll: 'Select from all clients',
    searchLabel: 'Search clients',
    changeClient: 'Change code sample language',
  },
  operation: {
    codeSampleUnavailable: 'No code sample available for this example.',
    body: 'Body',
    cookies: 'Cookies',
    headers: 'Headers',
    pathParameters: 'Path Parameters',
    queryParameters: 'Query Parameters',
    requestBody: 'Request Body',
    responses: 'Responses',
    testRequest: 'Test Request',
    webhook: 'Webhook',
    selectedContentType: 'Selected Content Type',
    callbacks: 'Callbacks',
  },
  response: {
    xmlGenerationLimit:
      'The XML example exceeds the generation limit. Supply a serialized XML example to display the complete payload.',
    xmlGenerationFailed: 'Unable to generate an XML example: {message}',
    exampleResponses: 'Example Responses',
    noBody: 'No Body',
    showSchema: 'Show Schema',
    status: 'Status',
  },
  schema: {
    noAllowedValues: 'No values satisfy this enum constraint.',
    example: 'Example',
    examples: 'Examples',
    default: 'Default',
    schema: 'Schema',
    emptyObject: 'Empty object',
    showAdditionalProperties: 'Show additional properties',
    forName: 'for {name}',
    showSchemaDetails: 'Show Schema Details',
    oneOf: 'One of',
    anyOf: 'Any of',
    allOf: 'All of',
    not: 'Not',
    unknownType: 'unknown type',
    propertyCount: 'Properties: {count}',
    headerCount: 'Headers: {count}',
    recursiveReference: 'Recursive reference to {name}',
    recursive: 'recursive',
    additionalProperties: 'additional properties',
    const: 'const',
    deprecated: 'deprecated',
    discriminator: 'Discriminator',
    enum: 'enum',
    format: 'Format',
    greaterThan: 'greater than',
    keys: 'keys',
    lessThan: 'less than',
    max: 'max',
    min: 'min',
    maxLength: 'max length',
    minLength: 'min length',
    multipleOf: 'multiple of',
    nullable: 'nullable',
    propertyNames: 'property names',
    pattern: 'Pattern',
    copyPattern: 'Copy pattern',
    readOnly: 'read-only',
    required: 'required',
    hideValues: 'Hide values',
    showAllValues: 'Show all values',
    type: 'Type',
    unique: 'unique',
    values: 'values',
    writeOnly: 'write-only',
  },
  download: {
    openapi: 'Download OpenAPI Document',
    asyncapi: 'Download AsyncAPI Document',
    asFormat: '{label} as {format}',
  },
  models: {
    label: 'Models',
  },
  actions: {
    copyAsMarkdown: 'Copy as Markdown',
    copied: 'Copied',
    copyMarkdownFailed: 'Failed to copy Markdown',

    copyLink: 'Copy link',
    copyLinkTo: 'Copy link to {name}',
    copyToClipboard: 'Copy link to clipboard',
    copyEndpointUrl: 'Copy endpoint URL',
    showMore: 'Show More',
  },
  agent: {
    askAi: 'Ask AI',
    askAiAgent: 'Ask AI Agent',
    close: 'Close Client',
  },
  mcp: {
    generate: 'Generate MCP',
    connect: 'Connect MCP',
  },
  developerTools: {
    title: 'Developer Tools',
    configure: 'Configure',
    share: 'Share',
    deploy: 'Deploy',
    scalarConfiguration: 'Scalar Configuration',
    theme: 'Theme',
    layout: 'Layout',
    layoutOptions: 'Layout Options',
    intro:
      'The developer tools allow you to customize the appearance and behavior of your documentation. You can also share your documentation using the Scalar Registry.',
    disableToolbarBefore: 'To disable the toolbar, set',
    disableToolbarAfter: 'in your configuration.',
    localhostOnly: 'The developer tools will only appear when running on localhost.',
    layoutModern: 'Modern',
    layoutClassic: 'Classic',
    showSidebar: 'Show Sidebar',
    defaultOpenFirstTag: 'Default Open First Tag',
    defaultOpenAllTags: 'Default Open All Tags',
    expandAll: 'Expand All {label}',
    expandAllResponses: 'Expand All Responses',
    hideClientButton: 'Hide Client Button',
    hideDarkModeToggle: 'Hide Dark Mode Toggle',
    hideModels: 'Hide {label}',
    hideSearch: 'Hide Search',
    showOperationId: 'Show Operation ID',
    hideTestRequestButton: 'Hide Test Request Button',
    scalarDocs: 'Scalar Docs',
    deployDescription:
      'Deploy your documentation on Scalar, the modern documentation platform for your API and everything else.',
    shareTitle: 'Share your API Reference',
    shareDescription:
      'Upload your OpenAPI document to share your API Reference with others. As easy as pressing a button.',
    uploadDocument: 'Upload Document',
    temporaryLinkExpiration: 'Your document will automatically be deleted after 7 days.',
    deployOnScalar: 'Deploy on Scalar',
    deployFree: 'Deploy your documentation for free.',
    additionalFeaturesMightRequire: 'Additional features might require',
    generate: 'Generate',
    passwordProtection: 'Password Protection',
    customDomains: 'Custom Domains',
    freeFormContent: 'Free-form content',
    cdnInfrastructure: 'CDN Infrastructure',
    pullFromGitHub: 'Pull from GitHub',
    markdownMdx: 'Markdown/MDX',
    spectralLinting: 'Spectral Linting',
    jsonSchemaHosting: 'JSON Schema Hosting',
    askAi: 'Ask AI',
    mcpServers: 'MCP Servers',
    unableToExportDocument: 'Unable to export active document',
    unknownError: 'An unknown error occurred',
  },
  gettingStarted: {
    swaggerEditor: 'Swagger Editor',
    description:
      'Welcome to the Scalar API References + Swagger Editor, a Free & Open-Source tool that takes your Swagger/OAS file and generates Beautiful API references.',
    showExample: 'Show Example',
    uploadFile: 'Upload File',
    integrations: 'INTEGRATIONS',
    theming: 'THEMING',
    features: 'Features',
    customize: 'Customize',
    customizeDescription: 'Bring your typography & color palettes, or use our themes!',
    testing: 'Testing',
    testingDescription: 'A deeply integrated Rest API Client (Also Free & Open-Source)',
    search: 'Search',
    searchDescription: 'Fully integrated Search (Using fuse.js)',
    hosting: 'Hosting',
    hostingDescription: 'Free subdomain hosting on https://apidocumentation.com',
    openApiSwagger: 'OpenAPI & Swagger',
    openApiSwaggerDescription: 'Support for OpenAPI 3.1, OpenAPI 3.0, and Swagger 2.0',
    codeSamples: 'Code Samples',
    codeSamplesDescription: 'Code samples to show off your API in most popular languages',
  },
  footer: {
    poweredByScalar: 'Powered by Scalar',
  },
  authentication: {
    detailsRequired: 'Authentication required',
    detailsOptional: 'Authentication optional',
    apiKey: 'API key',
    mutualTLS: 'Mutual TLS',
    apiKeyCookie: 'Send the API key in the “{name}” cookie.',
    apiKeyHeader: 'Send the API key in the “{name}” header.',
    apiKeyQuery: 'Send the API key in the “{name}” query parameter.',
    title: 'Authentication',
    accepts: 'Accepts',
    allOf: 'all of:',
    authentication: 'authentication',
    optional: 'Auth Optional',
    oneOf: 'one of:',
    required: 'Auth Required',
    requires: 'Requires',
    scopes: 'OAuth scopes',
  },
} satisfies ApiReferenceTranslations
